import { Link } from "react-router-dom";
import { CalendarClock, MapPin, Users, Sparkles, Ticket } from "lucide-react";
import { MaskText, MaskTextLoad, FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import Countdown from "@/components/Countdown";
import { EVENT, PAST_EDITIONS } from "@/data/content";

const ICONS = [CalendarClock, MapPin, Users, Ticket];

export default function Gather() {
  return (
    <div className="bg-cream">
      <section className="relative pt-40 md:pt-52 pb-16 px-5 md:px-10 grain overflow-hidden" data-testid="gather-hero">
        <div className="absolute -top-10 left-1/2 -translate-x-1/2 h-[26rem] w-[26rem] rounded-full brand-gradient blur-[140px] opacity-20 animate-floaty" />
        <div className="relative z-10 max-w-[1400px] mx-auto">
          <p className="font-display text-xs uppercase tracking-[0.3em] text-magenta mb-6">{EVENT.kicker}</p>
          <h1 className="font-serif font-light text-[12vw] md:text-[7vw] leading-[0.86] text-ink">
            <MaskTextLoad lines={["Where the movement", "comes to life."]} />
          </h1>
          <FadeUp delay={0.5}>
            <p className="mt-8 max-w-2xl font-sans text-lg md:text-xl leading-relaxed text-ink/65">{EVENT.intro}</p>
          </FadeUp>
        </div>
      </section>

      <Countdown />

      <section className="px-5 md:px-10 pb-8" data-testid="gather-details">
        <Stagger className="max-w-[1400px] mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {EVENT.details.map((d, i) => {
            const Icon = ICONS[i];
            return (
              <StaggerItem key={d.label}>
                <div className="h-full rounded-3xl border border-ink/10 bg-white card-shadow p-8 hover:-translate-y-1 transition-transform duration-500">
                  <Icon size={26} className="text-magenta" />
                  <p className="mt-8 font-display text-xs uppercase tracking-[0.22em] text-ink/45">{d.label}</p>
                  <p className="mt-2 font-serif text-3xl md:text-4xl text-ink">{d.value}</p>
                  <p className="mt-2 font-sans text-sm text-ink/50">{d.note}</p>
                </div>
              </StaggerItem>
            );
          })}
        </Stagger>
      </section>

      <section className="px-5 md:px-10 py-20 md:py-28 bg-cream" data-testid="past-editions">
        <div className="max-w-[1400px] mx-auto">
          <FadeUp>
            <p className="font-display text-xs uppercase tracking-[0.25em] text-magenta mb-4">{PAST_EDITIONS.kicker}</p>
            <h2 className="font-serif font-light text-4xl md:text-6xl text-ink leading-[0.95]">
              <MaskText lines={[PAST_EDITIONS.title]} />
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <p className="mt-6 max-w-2xl font-sans text-base md:text-lg leading-relaxed text-ink/65">{PAST_EDITIONS.intro}</p>
          </FadeUp>

          <Stagger className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {PAST_EDITIONS.editions.map((ed) => (
              <StaggerItem key={ed.no}>
                <div className={`group h-full rounded-3xl border bg-white card-shadow overflow-hidden ${ed.upcoming ? "border-magenta/40 ring-1 ring-magenta/20" : "border-ink/10"}`} data-testid={`edition-${ed.no}`}>
                  <div className={`relative overflow-hidden aspect-[16/11] ${ed.fit === "contain" ? "bg-[#1B0812]" : ""}`}>
                    <img src={ed.image} alt={ed.name} className={`h-full w-full transition-transform duration-700 ${ed.fit === "contain" ? "object-contain" : "object-cover group-hover:scale-105"}`} />
                    <span className="absolute top-4 left-4 font-serif text-3xl text-white drop-shadow">{ed.no}</span>
                    {ed.upcoming && (
                      <span className="absolute top-4 right-4 rounded-full brand-gradient px-3 py-1 font-display text-[0.6rem] uppercase tracking-[0.16em] font-bold text-white">Upcoming</span>
                    )}
                  </div>
                  <div className="p-7">
                    <p className="font-serif text-2xl text-ink">{ed.name}</p>
                    <p className="mt-1 font-sans text-sm text-ink/50">{ed.note}</p>
                    <div className="mt-5 flex items-center gap-6">
                      <div>
                        <p className="font-display text-[0.65rem] uppercase tracking-[0.18em] text-ink/40">Year</p>
                        <p className="font-serif text-lg text-ink">{ed.year}</p>
                      </div>
                      <div>
                        <p className="font-display text-[0.65rem] uppercase tracking-[0.18em] text-ink/40">Theme</p>
                        <p className="font-sans text-sm text-magenta">{ed.theme}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="px-5 md:px-10 py-20 md:py-28" data-testid="gather-speakers">
        <div className="max-w-[1400px] mx-auto">
          <FadeUp>
            <p className="font-display text-xs uppercase tracking-[0.25em] text-magenta mb-4">Voices on the Stage</p>
            <h2 className="font-serif font-light text-4xl md:text-6xl text-ink leading-[0.95]">
              <MaskText lines={["Speakers being curated."]} />
            </h2>
          </FadeUp>
          <Stagger className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {EVENT.speakers.map((s) => (
              <StaggerItem key={s}>
                <div className="group rounded-3xl border border-ink/10 bg-white card-shadow p-6 aspect-[4/5] flex flex-col justify-between">
                  <div className="h-14 w-14 rounded-full brand-gradient diamond-clip opacity-85 group-hover:opacity-100 transition-opacity" />
                  <div>
                    <p className="font-display text-xs uppercase tracking-[0.18em] text-ink/40">To be revealed</p>
                    <p className="mt-2 font-serif text-2xl text-ink leading-tight">{s}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="relative overflow-hidden py-24 md:py-32 px-5 md:px-10" data-testid="gather-sponsors">
        <div className="absolute inset-0 brand-gradient" />
        <div className="absolute inset-0 grain" />
        <div className="relative z-10 max-w-[1000px] mx-auto text-center">
          <Sparkles size={30} className="mx-auto text-white mb-6" />
          <p className="font-display text-xs uppercase tracking-[0.3em] text-white/75 mb-6">Become a Sponsor</p>
          <h2 className="font-serif font-light text-3xl md:text-5xl leading-tight text-white">
            <MaskText lines={["The future deserves preparation,", "not chance."]} />
          </h2>
          <FadeUp delay={0.12}>
            <p className="mt-8 max-w-2xl mx-auto font-sans text-lg leading-relaxed text-white/90">{EVENT.sponsors}</p>
          </FadeUp>
          <FadeUp delay={0.2}>
            <Link
              to="/register"
              data-testid="gather-sponsor-cta"
              className="mt-10 inline-flex rounded-full bg-[#1B0812] px-9 py-4 font-display text-sm uppercase tracking-[0.14em] font-bold text-cream transition-transform duration-300 hover:scale-105"
            >
              Partner With Us
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
